import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NOTIFICATION_SOUND = '/sounds/message.ogg';
const POLL_INTERVAL = 30000; // 30 seconds

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  // Initialize from localStorage
  const getInitialNotifications = () => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('notifications_enabled') === 'true';
  };
  
  const getInitialSound = () => {
    if (typeof window === 'undefined') return true;
    return localStorage.getItem('notification_sound_enabled') !== 'false';
  };
  
  const getInitialPermission = () => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      return Notification.permission === 'granted';
    }
    return false;
  };

  const [hasPermission, setHasPermission] = useState(getInitialPermission);
  const [notificationsEnabled, setNotificationsEnabled] = useState(getInitialNotifications);
  const [soundEnabled, setSoundEnabled] = useState(getInitialSound);
  const [unreadCount, setUnreadCount] = useState(0);
  const [newEmailsCount, setNewEmailsCount] = useState(0);
  
  const audioRef = useRef(null);
  const lastCheckRef = useRef(null);
  const intervalRef = useRef(null);
  const lastTotalRef = useRef(null);
  const lastBroadcastCheckRef = useRef(null);

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND);
    audioRef.current.volume = 0.7;
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Play notification sound
  const playSound = useCallback(() => {
    const soundAllowed = localStorage.getItem('notification_sound_enabled') !== 'false';
    if (audioRef.current && soundAllowed) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(err => {
        console.log('Could not play sound:', err.message);
      });
    }
  }, []);

  // Show browser notification
  const showBrowserNotification = useCallback((title, body) => {
    const notificationsAllowed = localStorage.getItem('notifications_enabled') === 'true';
    
    if ('Notification' in window && Notification.permission === 'granted' && notificationsAllowed) {
      const notification = new Notification(title, {
        body,
        icon: '/mpm-logo.png',
        badge: '/mpm-favicon.png',
        tag: 'mypatriotmail-new-email',
        renotify: true,
        requireInteraction: false,
        silent: true
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      setTimeout(() => notification.close(), 8000);
    }
  }, []);

  // Check for new emails
  const checkForNewEmails = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await axios.get(`${API}/emails/`, {
        params: { folder: 'inbox', limit: 5 }
      });

      const currentTotal = response.data.total || 0;
      const currentUnread = response.data.unread || 0;
      
      setUnreadCount(currentUnread);

      // Check if we have new emails since last check
      if (lastTotalRef.current !== null && currentTotal > lastTotalRef.current) {
        const newCount = currentTotal - lastTotalRef.current;
        setNewEmailsCount(prev => prev + newCount);
        
        // Get the latest email info
        const latestEmail = response.data.emails?.[0];
        const senderName = latestEmail?.from_name || latestEmail?.from_email || 'Someone';
        const subject = latestEmail?.subject || 'New message';
        
        // Play sound
        playSound();
        
        // Show notification
        showBrowserNotification(
          `New Email from ${senderName}`,
          subject
        );
        
        // Also show toast
        toast.info(`📬 New email from ${senderName}`, {
          description: subject.substring(0, 50) + (subject.length > 50 ? '...' : ''),
          duration: 5000
        });
      }

      lastTotalRef.current = currentTotal;
      lastCheckRef.current = Date.now();
      
      // Also check for broadcast notifications
      try {
        const broadcastResponse = await axios.get(`${API}/broadcasts/my`, {
          params: { unread_only: true }
        });
        
        const unreadBroadcasts = broadcastResponse.data || [];
        
        // Show notification for new broadcasts (only if we've checked before)
        if (lastBroadcastCheckRef.current !== null && unreadBroadcasts.length > 0) {
          const latestBroadcast = unreadBroadcasts[0];
          
          // Check if this is a new broadcast we haven't seen
          if (!latestBroadcast.is_read) {
            playSound();
            showBrowserNotification(
              `📢 ${latestBroadcast.title}`,
              latestBroadcast.message
            );
            
            toast.info(`📢 ${latestBroadcast.title}`, {
              description: latestBroadcast.message.substring(0, 100),
              duration: 8000
            });
          }
        }
        
        lastBroadcastCheckRef.current = Date.now();
      } catch (broadcastError) {
        // Silently fail for broadcasts
        console.log('Broadcast check failed:', broadcastError.message);
      }
      
    } catch (error) {
      // Silently fail - user might not be authenticated
      console.log('Email check failed:', error.message);
    }
  }, [playSound, showBrowserNotification]);

  // Start polling when user is authenticated
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Start polling - initial check happens on first interval
    const timeoutId = setTimeout(() => {
      checkForNewEmails();
    }, 1000); // Small delay for initial check
    
    intervalRef.current = setInterval(checkForNewEmails, POLL_INTERVAL);

    return () => {
      clearTimeout(timeoutId);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkForNewEmails]);

  // Clear new emails count
  const clearNewEmailsCount = useCallback(() => {
    setNewEmailsCount(0);
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      toast.error('Your browser does not support notifications');
      return false;
    }

    const permission = await Notification.requestPermission();
    const granted = permission === 'granted';
    setHasPermission(granted);
    
    if (granted) {
      setNotificationsEnabled(true);
      localStorage.setItem('notifications_enabled', 'true');
    }
    
    return granted;
  }, []);

  // Test notification
  const testNotification = useCallback(() => {
    playSound();
    showBrowserNotification('Test Notification', 'MyPatriotMail notifications are working!');
    toast.success('Test notification sent!');
  }, [playSound, showBrowserNotification]);

  const value = {
    hasPermission,
    notificationsEnabled,
    soundEnabled,
    unreadCount,
    newEmailsCount,
    setNotificationsEnabled,
    setSoundEnabled,
    clearNewEmailsCount,
    requestPermission,
    testNotification,
    playSound,
    checkForNewEmails
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

export default NotificationProvider;
